import React from 'react';
import { X, Edit, Calendar, Clock } from 'lucide-react';
import type { TaxRate } from '../../../../../types/taxRateMaintenance';
import './TaxRateDetailsModal.css';

interface TaxRateDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  taxRate: TaxRate | null;
  onEdit?: (taxRate: TaxRate) => void;
}

const TaxRateDetailsModal: React.FC<TaxRateDetailsModalProps> = ({
  isOpen,
  onClose,
  taxRate,
  onEdit
}) => {
  if (!isOpen || !taxRate) {
    return null;
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(taxRate);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="tax-rate-modal-overlay" onClick={onClose}>
      <div className="tax-rate-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="tax-rate-modal-header">
          <div className="tax-rate-modal-header-left">
            <div className="tax-rate-modal-icon">
              <div className={`status-dot ${taxRate.isActive ? 'active' : 'inactive'}`}></div>
            </div>
            <div className="tax-rate-modal-title-section">
              <h2 className="tax-rate-modal-title">Tax Rate Details</h2>
              <div className="tax-rate-modal-subtitle">
                {taxRate.taxRateValue} - {taxRate.description}
              </div>
            </div>
          </div>
          <div className="tax-rate-modal-header-actions">
            <button
              className="tax-rate-modal-edit-btn"
              onClick={handleEdit}
              title="Edit Tax Rate"
            >
              <Edit size={16} />
              Edit
            </button>
            <button
              className="tax-rate-modal-close-btn"
              onClick={onClose}
              title="Close Modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="tax-rate-modal-content">
          <div className="tax-rate-details-grid">
            {/* Basic Information */}
            <div className="tax-rate-detail-section">
              <h3 className="tax-rate-detail-section-title">Basic Information</h3>
              <div className="tax-rate-detail-grid">
                <div className="tax-rate-detail-item">
                  <label className="tax-rate-detail-label">Tax Rate Value</label>
                  <div className="tax-rate-detail-value tax-rate-value">{taxRate.taxRateValue}</div>
                </div>
                <div className="tax-rate-detail-item">
                  <label className="tax-rate-detail-label">Description</label>
                  <div className="tax-rate-detail-value">{taxRate.description}</div>
                </div>
                <div className="tax-rate-detail-item">
                  <label className="tax-rate-detail-label">Default Status</label>
                  <div className="tax-rate-detail-value">
                    <div className={`default-indicator ${taxRate.isDefault ? 'yes' : 'no'}`}>
                      {taxRate.isDefault ? 'Yes' : 'No'}
                      {taxRate.isDefault && <span className="default-badge">DEFAULT</span>}
                    </div>
                  </div>
                </div>
                <div className="tax-rate-detail-item">
                  <label className="tax-rate-detail-label">Status</label>
                  <div className="tax-rate-detail-value">
                    <div className={`status-indicator ${taxRate.isActive ? 'active' : 'inactive'}`}>
                      <div className={`status-dot ${taxRate.isActive ? 'active' : 'inactive'}`}></div>
                      {taxRate.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="tax-rate-detail-section">
              <h3 className="tax-rate-detail-section-title">System Information</h3>
              <div className="tax-rate-detail-grid">
                <div className="tax-rate-detail-item">
                  <label className="tax-rate-detail-label">
                    <Calendar size={14} />
                    Created Date
                  </label>
                  <div className="tax-rate-detail-value">{formatDate(taxRate.createdAt)}</div>
                </div>
                {taxRate.lastModified && (
                  <div className="tax-rate-detail-item">
                    <label className="tax-rate-detail-label">
                      <Clock size={14} />
                      Last Modified
                    </label>
                    <div className="tax-rate-detail-value">{formatDate(taxRate.lastModified)}</div>
                  </div>
                )}
                <div className="tax-rate-detail-item">
                  <label className="tax-rate-detail-label">Record ID</label>
                  <div className="tax-rate-detail-value system-id">{taxRate.id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="tax-rate-modal-footer">
          <div className="tax-rate-modal-footer-info">
            <span className="tax-rate-modal-footer-text">
              Tax Rate record for {taxRate.taxRateValue}
            </span>
          </div>
          <div className="tax-rate-modal-footer-actions">
            <button
              className="tax-rate-modal-btn secondary"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="tax-rate-modal-btn primary"
              onClick={handleEdit}
            >
              <Edit size={16} />
              Edit Tax Rate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxRateDetailsModal;